import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import {
  getDisplayCurrency,
  getAmountForCurrency,
  getConversion,
  getPreviousPeriodComparison,
  formatMoney,
} from '../../utils/statisticsHelpers';

interface StoreStatisticsWidgetProps {
  data: StatisticsSummaryView | null;
}

export const StoreStatisticsWidget: React.FC<StoreStatisticsWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const currency = getDisplayCurrency(data);

  const chartData = React.useMemo(() => {
    const salesByDay: any[] = data?.salesByDay ?? [];
    const visitorsByDay: any[] = data?.visitorsByDay ?? [];

    const dateMap = new Map<string, { date: string; revenue: number; orders: number; visitors: number }>();

    salesByDay.forEach((item: any) => {
      dateMap.set(item.date, {
        date: item.date,
        revenue: getAmountForCurrency(item.amountByCurrency, currency),
        orders: item.orderCount ?? 0,
        visitors: 0,
      });
    });

    visitorsByDay.forEach((item: any) => {
      const existing = dateMap.get(item.date);
      if (existing) {
        existing.visitors = item.visitorCount ?? 0;
      } else {
        dateMap.set(item.date, {
          date: item.date,
          revenue: 0,
          orders: 0,
          visitors: item.visitorCount ?? 0,
        });
      }
    });

    return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, currency]);

  const conversion = getConversion(data);
  const ordersTotal = conversion?.orderCount ?? 0;
  const visitorsTotal = conversion?.visitorCount ?? 0;
  const revenueTotal = chartData.reduce((sum, d) => sum + d.revenue, 0);

  // Previous period comparison
  const comparison = getPreviousPeriodComparison(data);
  const ordersChangePct = comparison?.changes?.ordersChangePercent;
  const revenueChangePct = comparison?.changes?.revenueChangePercentByCurrency?.[currency];
  const visitorsChangePct = comparison?.changes?.visitorsChangePercent;

  const renderChange = (pct: number | undefined) => {
    if (pct == null) return null;
    const isPositive = pct >= 0;
    const arrow = isPositive ? '\u2191' : '\u2193';
    return (
      <span className={`stat-metric-change ${isPositive ? 'positive' : 'negative'}`}>
        {arrow} {Math.abs(pct).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_store_statistics')}</h3>
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#6B7280" />
            <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="#6B7280" />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="#6B7280" />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === 'revenue') return [formatMoney(value, currency), t('dashboard_stats_revenue')];
                if (name === 'orders') return [value, t('dashboard_stats_orders')];
                if (name === 'visitors') return [value.toLocaleString(), t('dashboard_stats_visitors')];
                return [value, name];
              }}
              labelFormatter={(label) => String(label)}
            />
            <Legend
              formatter={(value: string) => {
                if (value === 'revenue') return t('dashboard_stats_revenue');
                if (value === 'orders') return t('dashboard_stats_orders');
                if (value === 'visitors') return t('dashboard_stats_visitors');
                return value;
              }}
            />
            <Area yAxisId="right" type="monotone" dataKey="visitors" stroke="#9CA3AF" fill="#9CA3AF" fillOpacity={0.1} strokeWidth={1} />
            <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#2563EB" fill="#2563EB" fillOpacity={0.3} strokeWidth={2} />
            <Area yAxisId="left" type="monotone" dataKey="orders" stroke="#059669" fill="#059669" fillOpacity={0.2} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="stat-metrics-row">
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_orders')}</span>
          <span className="stat-metric-value">{ordersTotal}</span>
          {renderChange(ordersChangePct)}
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_visitors')}</span>
          <span className="stat-metric-value">{visitorsTotal.toLocaleString()}</span>
          {renderChange(visitorsChangePct)}
        </div>
        <div className="stat-metric">
          <span className="stat-metric-label">{t('dashboard_stats_revenue')}</span>
          <span className="stat-metric-value">{formatMoney(revenueTotal, currency)}</span>
          {renderChange(revenueChangePct)}
        </div>
      </div>
    </div>
  );
};
